import type {
  MonthlyStatus,
  PrescriptionCard,
  YearlyMonth,
} from '../api/performance.api';

/** "202607" → 7 */
export const monthOf = (ym: string) => Number(ym.slice(4, 6));
/** (2026, 7) → "202607" */
export const toYm = (year: number, month: number) =>
  `${year}${String(month).padStart(2, '0')}`;

/* ───────────── 월 카드 상태 (M-08) ───────────── */

export type MonthCardStatus = MonthlyStatus | 'NOT_ARRIVED';

/** 월 카드 배지 문구 — SETTLED는 "정산 비율 n%"라 화면에서 비율을 붙인다 */
export const MONTHLY_STATUS_LABEL: Record<MonthCardStatus, string> = {
  SETTLED: '정산 비율',
  PENDING_CONFIRMATION: '처방 확인중',
  BEFORE_SETTLEMENT: '정산 전',
  UNREGISTERED: '처방 미등록',
  NOT_ARRIVED: '미도래',
};

/** KPI 「정산 전 · 미등록」 — 응답 월 카드 중 정산 전 · 처방 미등록인 달 */
export const getPendingMonths = (months: YearlyMonth[]) =>
  months
    .filter(
      (m) =>
        m.monthlyStatusCode === 'BEFORE_SETTLEMENT' ||
        m.monthlyStatusCode === 'UNREGISTERED',
    )
    .map((m) => ({
      month: monthOf(m.settlementYearMonth),
      status: m.monthlyStatusCode,
    }));

/* ───────────── 추이 차트 (M-08 · M-09) ───────────── */

/** 차트 한 달 값 — 응답에 있는 달만 넘긴다 */
export interface TrendPoint {
  ym: string;
  estimated: number | null;
  /** 막대 — 정산이 끝난 달만 값이 있다 */
  confirmed: number | null;
  /** 처방 미등록 — 0 위치에 회색 점 */
  unregistered?: boolean;
}

/** 연간 월 카드 → 차트 값 (미등록 달은 회색 점, 정산 막대는 SETTLED 달만) */
export const toYearlyTrendPoints = (months: YearlyMonth[]): TrendPoint[] =>
  months.map((m) => {
    const unregistered = m.monthlyStatusCode === 'UNREGISTERED';
    return {
      ym: m.settlementYearMonth,
      estimated: unregistered ? 0 : m.estimatedAmountPrescription,
      confirmed:
        m.monthlyStatusCode === 'SETTLED' ? m.confirmedAmountSettlement : null,
      unregistered,
    };
  });

/* ───────────── 처방 카드 상태 배지 (M-09 · M-17 · M-18) ───────────── */

export type LineBadgeTone =
  'success' | 'primary' | 'warning' | 'danger' | 'muted';

export interface LineBadge {
  label: string;
  tone: LineBadgeTone;
}

type StatusKind =
  | 'APPROVED'
  | 'UNREGISTERED'
  | 'NON_TRADABLE'
  | 'MIN_AMOUNT'
  | 'ABSORPTION'
  | 'EDI';

/**
 * 상태 코드 · 문구 → 판정용 종류. 처방 승인 코드는 문서에 있는 4종이고,
 * 정산 상태 코드는 목록이 정해지지 않아 표시 문구(승인 · 정산불가 EDI · 흡수율 미달 …)로도 맞춘다.
 */
const kindOf = (
  code: string | null | undefined,
  label: string | null | undefined,
): StatusKind | null => {
  if (!code && !label) return null;
  const c = code ?? '';
  const l = label ?? '';
  if (c === 'APPROVED' || l === '승인') return 'APPROVED';
  if (c === 'UNREGISTERED_BUSINESS' || l.includes('미등록 거래처'))
    return 'UNREGISTERED';
  if (c === 'NON_TRADABLE_DRUG' || l.includes('거래 불가'))
    return 'NON_TRADABLE';
  if (c === 'BELOW_MINIMUM_AMOUNT' || l.includes('최소금액'))
    return 'MIN_AMOUNT';
  if (c.includes('EDI') || l.includes('EDI')) return 'EDI';
  if (c.includes('ABSORPTION') || l.includes('흡수율')) return 'ABSORPTION';
  return null;
};

const rxKind = (line: PrescriptionCard) =>
  kindOf(line.approvalStatusCode, line.approvalStatus);
const stKind = (line: PrescriptionCard) =>
  kindOf(line.settlementStatusCode, line.settlementStatus);

/**
 * 처방 카드 상태 배지 — 앱 판정 규칙을 그대로 옮겼다 (docs/api/performance.md 표).
 * 위에서부터 먼저 맞는 것 하나만 보여준다. 처방 · 정산이 모두 승인이면 배지가 없다.
 */
export const getLineBadge = (line: PrescriptionCard): LineBadge | null => {
  const rx = rxKind(line);
  const st = stKind(line);
  const hasSettlement = !!(line.settlementStatusCode || line.settlementStatus);

  if (rx === 'NON_TRADABLE' || st === 'NON_TRADABLE')
    return { label: '거래 불가 의약품', tone: 'muted' };
  if (st === 'EDI') return { label: '정산불가 EDI', tone: 'muted' };
  if (rx === 'UNREGISTERED' || st === 'UNREGISTERED')
    return { label: '미등록 거래처', tone: 'danger' };
  if (st === 'ABSORPTION') return { label: '흡수율 미달', tone: 'warning' };
  // 최소금액 미달이어도 제약사가 승인하면 정산된다
  if (rx === 'MIN_AMOUNT' && st === 'APPROVED')
    return { label: '제약사 정산', tone: 'primary' };
  if (rx === 'MIN_AMOUNT' || st === 'MIN_AMOUNT')
    return { label: '최소금액 미달', tone: 'warning' };
  if (!hasSettlement) return { label: '정산 전', tone: 'muted' };
  if (rx !== 'APPROVED' && st === 'APPROVED')
    return { label: '제약사 승인', tone: 'success' };
  return null;
};

/** 거래 불가 의약품은 카드 전체를 흐리게 보여준다 */
export const isLineDisabled = (line: PrescriptionCard) =>
  rxKind(line) === 'NON_TRADABLE' || stKind(line) === 'NON_TRADABLE';

/** 최소금액 미달로 예상 금액이 없으면 그 자리에 "최소금액 기준 미달"을 쓴다 */
export const isBelowMinimumEstimate = (line: PrescriptionCard) =>
  rxKind(line) === 'MIN_AMOUNT' &&
  (line.estimatedAmount === null || line.estimatedAmount === 0);
